import request from './request'
import { getMockCouponUsageMetaResponse, shouldUseCouponMock } from './couponCenterMock'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface CouponUsageMetaOption {
  value: string
  label: string
}

type MetaOptionRaw = { value: string; label: string } | string

export interface CouponUsageMetaData {
  search_types?: MetaOptionRaw[]
  [key: string]: unknown
}

export const DEFAULT_USAGE_SEARCH_TYPES: CouponUsageMetaOption[] = [
  { value: 'coupon_code', label: 'Coupon Code' },
  { value: 'applied_order_id', label: 'Applied Order ID' },
  { value: 'user_mobile', label: 'User Mobile' },
]

function normalizeMetaOption(
  entry: MetaOptionRaw,
  fallbacks: CouponUsageMetaOption[],
): CouponUsageMetaOption | null {
  if (typeof entry === 'string') {
    const value = entry.trim()
    if (!value) return null
    const fallback = fallbacks.find((option) => option.value === value)
    return { value, label: fallback?.label ?? value }
  }

  const value = String(entry.value ?? '').trim()
  const label = String(entry.label ?? '').trim()
  if (!value) return null
  return { value, label: label || value }
}

function buildOptions(
  raw: MetaOptionRaw[] | undefined,
  fallbacks: CouponUsageMetaOption[],
): CouponUsageMetaOption[] {
  if (!Array.isArray(raw) || !raw.length) {
    return [...fallbacks]
  }

  const options = raw
    .map((entry) => normalizeMetaOption(entry, fallbacks))
    .filter((option): option is CouponUsageMetaOption => option != null)

  return options.length ? options : [...fallbacks]
}

export function buildUsageSearchTypes(
  meta: CouponUsageMetaData | null | undefined,
): CouponUsageMetaOption[] {
  return buildOptions(meta?.search_types, DEFAULT_USAGE_SEARCH_TYPES)
}

/** POST `/api/admin/coupon/usage-meta` */
export async function postCouponUsageMeta(): Promise<ApiResponse<CouponUsageMetaData>> {
  if (shouldUseCouponMock()) {
    return getMockCouponUsageMetaResponse() as ApiResponse<CouponUsageMetaData>
  }
  return request.post<ApiResponse<CouponUsageMetaData>>('/api/admin/coupon/usage-meta', {})
}
